import { gql } from "@apollo/client";
import { faPen, faRefresh, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ComponentStateTypes } from "components/common/ComponentState";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import Error from "components/common/Error";
import Loading from "components/common/loading/Loading";
import NoData from "components/common/NoData";
import Page, { PageActionsType } from "components/common/page/Page";
import Tab from "components/common/tabs/Tab";
import Tabs from "components/common/tabs/Tabs";
import ItemView, { ItemViewPropertyViewDataListType } from "components/common/views/ItemView";
import MoneyView from "components/common/views/MoneyView";
import SaleDatalist from "components/entities/agriculture/sale/SaleDatalist";
import PersonPropertyView, { resolvePersonFromGraphQLQuery } from "components/entities/app/person/PersonPropertyView";
import WalletTransactionsDatalist from "components/entities/app/person/wallet/WalletTransactionsDatalist";
import { graph } from "config/axios-context";
import { AppRoutes, resolveRoute } from "config/router";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GetWalletViewPageQuery, SaleFilterInput } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";

const query = gql`
query GetWalletViewPage($id: Int!) {
    wallet(id: $id) {
         id,
         name,
         balance,
         note,
         archived,
         deposit,
         person {
            id,
            name,
            contactInfo {
                items {
                    value
                    name
                    link
                }
            }
            tags {
                items {
                    tag {
                        name
                    }
                }
            }
         },
         sales {
            count,
            totalSum
         },
         transactions {
          count
          amountSum
          items {
            amount
            transactionId
            transactionDate
            note
            id
          }
         }
    }
}
`;


const DELETE_MUTATION = `
    mutation WalletDelete($id: Int) {
        walletDelete(id: $id)
    }
`;

const WalletViewPage = () => {
    const context = useAppContext();
    const params = useParams<{ id: string }>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [model, setModel] = useState<GetWalletViewPageQuery | undefined | null>(null);

    useEffect(() => {
        loadData().then();
    }, [params.id]);

    const saleFixedFilters: SaleFilterInput = {
        walletId: {
            eq: parseInt(params.id || '0') || 0
        }
    }

    const pageActions: PageActionsType = [
        {
            icon: faRefresh,
            link() {
                loadData().then();
            },
            name: 'refresh',
            title: 'button.reload'
        },
        {
            icon: faPen,
            name: 'edit',
            title: 'button.edit',
            link: resolveRoute(AppRoutes.PERSON_WALLET_EDIT, { id: params.id }),
        },
        {
            icon: faTrash,
            link() {
                context.modal.open('wallet-view-delete', params.id);
            },
            name: 'wallet-view-delete',
            title: 'button.delete',
            className: 'text-danger'
        }
    ]

    const loadData = async (action?: string) => {
        if (componentState !== 'loading') {
            setComponentState('loading');
        }

        try {
            const response = await graph({
                query: query,
                variables: {
                    id: parseInt(params.id || '0')
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            setModel(response.data);
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 't1', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }

    const getViewData = () => {
        const m = model?.wallet;
        if (componentState === 'loading') return <Loading />;
        if (componentState === 'error') return <Error message="error" />;
        if (!m) return <NoData />;

        const viewProps: ItemViewPropertyViewDataListType[] = [
            {
                name: 'wallet',
                items: [
                    { // id
                        name: 'id',
                        label: 'fields.id.name',
                        value: m.id,
                    },
                    { // person
                        name: 'person',
                        label: 'fields.person.name',
                        value: <PersonPropertyView
                            {...resolvePersonFromGraphQLQuery(m.person!)}
                            open
                            copy
                            name="wallet-view-page-person"
                        />,
                    },
                    { // name
                        name: 'name',
                        label: 'fields.name.name',
                        value: m.name,
                    },
                    { // balance
                        name: 'balance',
                        label: 'fields.balance.name',
                        value: <MoneyView value={m.balance} />,
                    },
                    { // deposit
                        name: 'deposit',
                        label: 'fields.deposit.name',
                        value: <MoneyView value={m.deposit} />,
                    },
                    { // transactions
                        name: 'transactions',
                        label: 'money.transactions',
                        value: m.transactions.count
                            ? <span><MoneyView value={m.transactions.amountSum} /> ({m.transactions?.count})</span>
                            : <span>-</span>,
                    },
                    { // sales
                        name: 'sales',
                        label: 'pages.agriculture.sale.list',
                        value: <span><MoneyView value={m.sales.totalSum} /> ({m.sales?.count})</span>,
                    },
                    { // archived
                        name: 'archived',
                        label: 'fields.archived.name',
                        value: m.archived,
                        propertyType: 'bool',
                    },
                    { // note
                        name: 'note',
                        label: 'fields.note.name',
                        value: m.note,
                        propertyType: "note"
                    }
                ]
            }
        ];

        return <ItemView model={viewProps} view="default" name="wallet-view-page" />;
    }

    const onDelete = async () => {
        try {
            const response = await graph({
                query: DELETE_MUTATION,
                variables: { id: parseInt(params.id || '0') }
            });

            if (response.errors) {
                throw response.errors;
            }

            context.notify('success', 'request.delete.success');
            context.navigate(AppRoutes.PERSON_WALLET_DATALIST);
        } catch (error: any) {
            context.notify('error', 'request.delete.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        }
    }

    return (
        <>
            <Page title="pages.people.wallet.view" wFull actions={pageActions}>
                <Tabs>
                    <Tab title="common.info" name="info">
                        {getViewData()}
                    </Tab>
                    <Tab title="money.transactions" name="transactions">
                        <WalletTransactionsDatalist
                            onItemChange={loadData}
                            name="wallet-view"
                            walletId={parseInt(params.id || '0')}
                        />
                    </Tab>
                    <Tab title="pages.agriculture.sale.list" name="sales">
                        <SaleDatalist fixedFilters={saleFixedFilters} />
                    </Tab>
                </Tabs>
            </Page>
            <ConfirmationModal
                name="wallet-view-delete"
                text="confirmations.deleteItremConfirmation"
                onConfirm={onDelete}
            />
        </>
    );
};

export default WalletViewPage;
